import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { T as TransitionPromise } from './TransitionPromise.js';
import { d as defineCustomElement$3 } from './icon.js';
import { d as defineCustomElement$2 } from './icon-button.js';

const panedMenuCss = ":host{position:relative;box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:block;height:100%}.paned-menu{--overflow-margin:16px;height:100%;position:relative;overflow-x:clip;margin-inline:calc(-1 * var(--overflow-margin));padding-inline:var(--overflow-margin)}.paned-menu::after{content:\"\";z-index:2;pointer-events:none;position:absolute;inset:0;background-image:linear-gradient(90deg, white 0%, transparent var(--overflow-margin), transparent calc(100% - var(--overflow-margin)), white 100%)}.header{display:flex;flex-direction:row;justify-content:space-between;align-items:center}.close{display:flex;align-items:center;color:#aaa}.title{font-family:var(--tt-font-default);font-style:normal;font-weight:600;font-size:16px;line-height:32px;color:#121212}.back-arrow-title{display:flex;gap:8px;align-items:center}.back-icon{width:36px;font-size:20px;color:#aaa;margin-left:-10px;transition:width 500ms var(--tt-animation-easing-smooth), opacity 300ms linear 200ms;overflow:clip;opacity:1}.back-icon.back-icon--hidden{transition:width 500ms var(--tt-animation-easing-smooth), opacity 300ms linear;width:0;opacity:0}.panes{height:100%;position:relative;display:grid;isolation:isolate}::slotted(tt-menu-pane){width:calc(100% + 0.5rem);grid-column:1/-1;grid-row:1/-1;transition-property:transform;transition-timing-function:var(--tt-animation-easing-smooth)}::slotted(tt-menu-pane:not(.animated-pane)){transition:none !important}::slotted(.hidden-pane),::slotted(.hidden-history-pane){visibility:hidden !important}::slotted(.animated-pane){transform:translateX(0)}::slotted(.animated-pane.hiding-pane){transform:translateX(-120%)}::slotted(.animated-pane.hidden-pane){transition:transform 0s !important;transform:translateX(120%)}::slotted(.animated-pane.hiding-history-pane){transform:translateX(120%)}::slotted(.animated-pane.hidden-history-pane){transition:transform 0s !important;transform:translateX(-120%)}";

const PanedMenu = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.ttChangeEvent = createEvent(this, "tt-change", 7);
    this.menuClose = createEvent(this, "tt-menu-close", 7);
    this.panes = {};
    this.history = [];
    this.queuedTimeouts = [];
    this.transitionPromise = new TransitionPromise({ strict: false });
    this.parentDrawer = null;
    this.onSlotChange = (evt) => {
      const { target } = evt;
      const assignedElements = target.assignedElements().filter((node) => node.nodeName === 'TT-MENU-PANE');
      this.panes = {};
      assignedElements.forEach((element) => {
        const paneName = element.name;
        if (!this.currentPane)
          this.currentPane = paneName;
        this.panes[paneName] = element;
        if (paneName !== this.currentPane) {
          element.classList.add('hidden-pane');
        }
      });
      this.handleAnimationTime(this.animationTime);
      this.updateTitle();
    };
    this.title = '';
    this.currentPane = '';
    this.animationTime = 500;
    this.showCloseButton = false;
    this.goBackHistoryPane = this.goBackHistoryPane.bind(this);
    this.goBackToInitialPane = this.goBackToInitialPane.bind(this);
    this.updateParentDrawer = this.updateParentDrawer.bind(this);
    this.handleParentDrawerGoBack = this.handleParentDrawerGoBack.bind(this);
    this.handleParentDrawerHide = this.handleParentDrawerHide.bind(this);
    this.onNavigate = this.onNavigate.bind(this);
  }
  connectedCallback() {
    // componentWillLoad() {
    this.parentDrawer = this.element.closest('tt-drawer');
    if (this.parentDrawer) {
      this.parentDrawer.addEventListener('tt-go-back', this.handleParentDrawerGoBack);
      this.parentDrawer.addEventListener('tt-after-hide', this.handleParentDrawerHide);
      this.updateParentDrawer();
    }
  }
  disconnectedCallback() {
    if (this.parentDrawer) {
      this.parentDrawer.removeEventListener('tt-go-back', this.handleParentDrawerGoBack);
      this.parentDrawer.removeEventListener('tt-after-hide', this.handleParentDrawerHide);
    }
  }
  handleParentDrawerHide(event) {
    if (event.target === this.parentDrawer) {
      this.goBackToInitialPane();
      this.emitChangeEvent();
    }
  }
  handleParentDrawerGoBack(event) {
    if (event.target === this.parentDrawer) {
      this.goBackHistoryPane();
      this.emitChangeEvent();
    }
  }
  handleCurrentPaneChange(newValue, oldValue) {
    var _a, _b;
    //TODO: edge cases left to tackle like circular menus and
    this.updateTitle();
    this.clearTimeouts();
    const nPane = this.panes[newValue];
    if (nPane) {
      nPane.classList.remove('hiding-pane');
      nPane.classList.remove('hiding-history-pane');
    }
    const cPane = this.panes[oldValue];
    if (cPane) {
      if (oldValue === this.history.at(-1)) {
        if (this.animationTime > 0) {
          cPane.classList.add('hiding-pane');
          this.queuedTimeouts.push(window.setTimeout(() => {
            cPane.classList.add('hidden-history-pane');
            cPane.classList.remove('hiding-pane');
          }, this.animationTime));
        }
        else {
          cPane.classList.add('hidden-history-pane');
        }
      }
      else {
        if (this.animationTime > 0) {
          cPane.classList.add('hiding-history-pane');
          this.queuedTimeouts.push(window.setTimeout(() => {
            cPane.classList.add('hidden-pane');
            cPane.classList.remove('hiding-history-pane');
          }, this.animationTime));
        }
        else {
          cPane.classList.add('hidden-pane');
        }
      }
    }
    (_a = this.panes[this.currentPane]) === null || _a === void 0 ? void 0 : _a.classList.remove('hidden-pane');
    (_b = this.panes[this.currentPane]) === null || _b === void 0 ? void 0 : _b.classList.remove('hidden-history-pane');
    this.updateParentDrawer();
  }
  updateTitle() {
    var _a;
    this.title = ((_a = this.panes[this.currentPane]) === null || _a === void 0 ? void 0 : _a.headerTitle) || String.fromCharCode(65279);
    this.updateParentDrawer();
  }
  updateParentDrawer() {
    if (this.parentDrawer) {
      this.parentDrawer.label = this.title;
      this.parentDrawer.canGoBack = this.history.length > 0;
    }
  }
  clearTimeouts() {
    let timeout;
    while ((timeout = this.queuedTimeouts.pop())) {
      window.clearTimeout(timeout);
    }
  }
  emitChangeEvent() {
    this.ttChangeEvent.emit({
      pane: this.currentPane,
      paneStack: [...this.history],
    });
  }
  /** Shows the selected pane  */
  async changePane(paneName, bypassHistory = false) {
    if (paneName === this.currentPane)
      return;
    if (!bypassHistory) {
      this.history.push(this.currentPane);
    }
    this.currentPane = paneName;
    await this.transitionPromise.end();
  }
  /** Going back using history */
  async goBackHistoryPane() {
    if (this.history.length > 0) {
      const pane = this.history.pop();
      if (pane) {
        this.currentPane = pane;
        await this.transitionPromise.end();
      }
    }
  }
  /** Go all the way back in history to the first opened pane */
  async goBackToInitialPane() {
    if (this.history.length > 0) {
      this.currentPane = this.history[0];
      this.history = [];
      await this.transitionPromise.end();
    }
  }
  onNavigate(evt) {
    if (evt.target.tagName.toLowerCase() === 'tt-pane-navigation') {
      this.changePane(evt.detail.item.pane);
      this.emitChangeEvent();
    }
  }
  /** Going back using history */
  async getHistory() {
    return this.history;
  }
  handleAnimationTime(newValue) {
    Object.values(this.panes).forEach((element) => {
      element.style.transitionDuration = `${newValue}ms`;
      if (newValue > 0) {
        element.classList.add('animated-pane');
      }
      else {
        element.classList.remove('animated-pane');
      }
    });
  }
  render() {
    return (h("div", { class: "paned-menu", "data-pane": this.currentPane, "onTt-navigate": this.onNavigate }, !this.parentDrawer && (h("div", { class: "header" }, h("div", { class: "back-arrow-title" }, h("tt-icon-button", { class: `back-icon${this.history.length > 0 ? '' : ' back-icon--hidden'}`, label: "go back to the previous menu", library: "23", name: "arrow-left", onClick: () => {
        this.goBackHistoryPane();
        this.emitChangeEvent();
      } }), h("h2", { class: "title" }, this.title)), this.showCloseButton && (h("tt-icon-button", { class: "close", label: "close the menu", library: "23", name: "close", onClick: () => this.menuClose.emit() })))), h("div", { class: "panes", onTransitionEnd: this.transitionPromise.handleTransitionEnd }, h("slot", { onSlotchange: this.onSlotChange }))));
  }
  get element() { return this; }
  static get watchers() { return {
    "currentPane": ["handleCurrentPaneChange"],
    "animationTime": ["handleAnimationTime"]
  }; }
  static get style() { return panedMenuCss; }
}, [1, "tt-paned-menu", {
    "currentPane": [1537, "current-pane"],
    "animationTime": [2, "animation-time"],
    "showCloseButton": [4, "show-close-button"],
    "title": [32],
    "changePane": [64],
    "goBackHistoryPane": [64],
    "goBackToInitialPane": [64],
    "getHistory": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["tt-paned-menu", "tt-icon", "tt-icon-button"];
  components.forEach(tagName => { switch (tagName) {
    case "tt-paned-menu":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, PanedMenu);
      }
      break;
    case "tt-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "tt-icon-button":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const TtPanedMenu = PanedMenu;
const defineCustomElement = defineCustomElement$1;

export { TtPanedMenu, defineCustomElement };
